import { cva } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Split out of button.tsx so that file exports components only.
 *
 * React Fast Refresh can only hot-swap a module whose exports are all
 * components. Mixing `buttonVariants` (a function) in with `<Button>` silently
 * downgrades the whole module to a full reload on every edit — which is exactly
 * the file you edit most while styling.
 *
 * `ConfirmDialog` and the AlertDialog actions import these to look like buttons
 * without *being* buttons (Radix renders its own elements), which is why the
 * variants have to be shareable in the first place.
 */
export const buttonVariants = cva(
  // `active:scale-[0.98]` is the whole trick behind buttons that feel physical
  // rather than flat. Nobody notices it; its absence is why most dashboards
  // feel cheap.
  cn(
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium',
    'transition-all duration-150 active:scale-[0.98]',
    'disabled:pointer-events-none disabled:opacity-50',
    'focus-visible:ring-ring focus-visible:ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
    '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  ),
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-sm',
        outline:
          'border-input bg-background hover:bg-accent hover:text-accent-foreground border shadow-sm',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80 shadow-sm',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-6',
        icon: 'size-9',
        'icon-sm': 'size-8',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);
